<template>
    <div class="session__edit_completions">
        <component-title :size="'medium'">{{ $t('ebs.sessions.edit.title.completion') }}</component-title>
        <form-radio
            :disabled="isEditMode && (!isSessionUpcoming && !isSessionInProgress && !isSessionDraft)"
            :label="$t('ebs.sessions.edit.labels.calculate')"
            :name="'calculate-session-completion'"
            :items="[
                {
                    name: this.$t('ebs.sessions.edit.name.attendance'),
                    value: 'attendance'
                }
            ]"
            :hints="[this.$t('ebs.sessions.edit.text.sessionAreCompleted')]"
            :value="sessionCompletionType === 'attendance' || sessionCompletionType === 'automatic' ? 'attendance' : sessionCompletionType"
            @input="setSessionCompletionType"
        />
        <transition-group name="fade" mode="out-in" tag="div">
            <template v-if="sessionCompletionType === 'attendance' || sessionCompletionType === 'automatic'">
                <component-spacing :margin-top="'-3rem'" :key="'component-spacing'"/>
                <form-radio
                    :disabled="isEditMode && (!isSessionUpcoming && !isSessionInProgress && !isSessionDraft)"
                    :key="'calculate-session-completion'"
                    :label="''"
                    :name="'calculate-session-completion'"
                    :items="[
                        {
                            name: this.$t('ebs.sessions.edit.name.whenSessionEnds'),
                            value: 'automatic'
                        },
                        {
                            name: this.$t('ebs.sessions.edit.name.whenAllRegisters'),
                            value: 'attendance'
                        }
                    ]"
                    :overlay="true"
                    v-model="sessionCompletionType"
                />
            </template>
        </transition-group>
        <component-spacing :margin-top="'-2rem'"/>
        <form-radio
            :disabled="isEditMode && (!isSessionUpcoming && !isSessionInProgress && !isSessionDraft)"
            :label="''"
            :name="'calculate-session-completion'"
            :items="[
                {
                    name: this.$t('ebs.sessions.edit.name.trainerAssessment'),
                    value: 'assessment'
                }
            ]"
            :hints="[this.$t('ebs.sessions.edit.text.learnersAreMarked')]"
            v-model="sessionCompletionType"
        />
        <component-title :size="'medium'">{{ $t('ebs.sessions.edit.title.registers') }}</component-title>
        <form-checkbox
            :disabled="(isEditMode && (!isSessionUpcoming && !isSessionInProgress && !isSessionDraft)) || sessionCompletionType === 'attendance' || sessionCompletionType === 'automatic'"
            :name="'enable-registers'"
            :label="$t('ebs.sessions.edit.labels.enableRegisters')"
            v-model="sessionCompletionEnableRegisters"
        > {{ $t('ebs.sessions.edit.text.registersAreWhere') }}
        </form-checkbox>
        <component-title :size="'small'">{{ $t('ebs.sessions.edit.title.whoCan') }}</component-title>
        <form-checkbox
            :name="'session-coordinators'"
            :label="$t('ebs.sessions.edit.labels.sessionCoordinators')"
            :value="true"
            :disabled="true"
        />
        <component-spacing :margin-top="'-2.5rem'"/>
        <form-radio
            :disabled="isEditMode && (!isSessionUpcoming && !isSessionInProgress && !isSessionDraft)"
            :label="''"
            :name="'session-coordinators-any'"
            :items="[
                {
                    name: this.$t('ebs.sessions.edit.name.assignedTo'),
                    value: 'false'
                },
                {
                    name: this.$t('ebs.sessions.edit.name.anySession'),
                    value: 'true'
                }
            ]"
            :overlay="true"
            v-model="sessionCompletionRegistersEditByAnyCoordinator"
        />
        <component-spacing :margin-top="'-3rem'"/>
        <form-checkbox
            :disabled="(isEditMode && (!isSessionUpcoming && !isSessionInProgress && !isSessionDraft)) || !sessionEnableTrainers"
            :name="'trainers'"
            :label="$t('ebs.sessions.edit.labels.trainers')"
            v-model="sessionCompletionRegistersEditByTrainers"
        />
        <component-title :size="'medium'">{{ $t('ebs.sessions.edit.title.absenceReasons') }}</component-title>
        <form-checkbox
            :disabled="isEditMode && (!isSessionUpcoming && !isSessionInProgress && !isSessionDraft)"
            :label="$t('ebs.sessions.edit.labels.askAbsenceReason')"
            :name="'absence-booking-reason'"
            :hint="$t('ebs.sessions.edit.text.askLearnersAbsent')"
            v-model="sessionCompletionAbsenceReasonRequired"
        />
        <transition-group name="fade" mode="out-in">
            <form-textarea
                :disabled="isEditMode && (!isSessionUpcoming && !isSessionInProgress && !isSessionDraft)"
                :key="'absence-booking-reason-items'"
                v-if="sessionCompletionAbsenceReasonRequired"
                :label="$t('ebs.sessions.edit.labels.absenceReason')"
                :name="'absence-booking-reason-items'"
                :required="true"
                :data-vv-as="$t('forms.dataVvAs.absenceReason')"
                :hint="$t('ebs.sessions.edit.text.addANewReason')"
                :v-validate="'required'"
                v-model="sessionCompletionAbsenceReason"
            >{{ $t('ebs.sessions.edit.text.addANewReason') }}
            </form-textarea>
        </transition-group>
        <component-title :size="'medium'">{{ $t('ebs.sessions.edit.title.feedback') }}</component-title>
        <form-checkbox
            :disabled="(isEditMode && (!isSessionUpcoming && !isSessionInProgress && !isSessionDraft)) || !sessionEnableTrainers"
            :label="$t('ebs.sessions.edit.labels.askTrainersToLeave')"
            :name="'ask-trainers-leave-feedback'"
            v-model="sessionCompletionFormRequired"
        > {{ $t('ebs.sessions.edit.text.trainersWillLeave') }}
        </form-checkbox>
        <transition name="fade" mode="out-in">
            <template
                v-if="sessionCompletionFormRequired"
            >
                <form-customform
                    :disabled="isEditMode && (!isSessionUpcoming && !isSessionInProgress && !isSessionDraft)"
                    :title="$t('ebs.sessions.edit.title.configureFeedback')"
                    :items="sessionCompletionFormFields"
                    @emit-notify-add="sessionAddCompletionFormField"
                    @emit-notify-edit="sessionEditCompletionFormField"
                    @emit-notify-remove="sessionRemoveCompletionFormField"
                    @emit-move="sessionMoveCompletionFormField"
                    :name="'session-configure-feedback'"/>
            </template>
        </transition>
        <pop-notify
            ref="popnotifyvalidation"
            :class="'pop__notify--validation'"
            :title-text="`Something's not quite right`"
            :content-text="'Please check that you have filled out all the fields in the form correctly and try again.'"
            :cancel-button-text="'Ok'"
            :submit-button-active="false"
        />
    </div>
</template>

<script>
import FormCheckbox from '@/components/default/forms/FormCheckbox'
import FormRadio from '@/components/default/forms/FormRadio'
import FormCustomform from '@/components/default/forms/FormCustomform'
import PopNotify from '@/components/default/shared/PopNotify'
import ComponentTitle from '@/components/default/shared/ComponentTitle'
import ComponentSpacing from '@/components/default/shared/ComponentSpacing'
import FormTextarea from '@/components/default/forms/FormTextarea'
import { performReset, performSubmitValidate, validateBeforeRouteLeave, watchContentChangeEmit } from '@/helpers/mixins'

export default {
  components: {
    FormTextarea,
    ComponentSpacing,
    ComponentTitle,
    PopNotify,
    FormCustomform,
    FormRadio,
    FormCheckbox
  },
  name: 'session-edit-completions',
  mixins: [validateBeforeRouteLeave, performReset, performSubmitValidate, watchContentChangeEmit],
  computed: {
    rootItemName () {
      return 'session'
    },
    isEditMode () {
      return this.$route.name.includes('session-edit')
    },
    isSessionDraft () {
      return this.sessionStatus === 'draft'
    },
    isSessionUpcoming () {
      return this.sessionStatus === 'upcoming'
    },
    isSessionInProgress () {
      return this.sessionStatus === 'in_progress'
    },
    sessionStatus () {
      return this.$store.getters.getSessionDefaultStatus
    },
    sessionEnableTrainers: {
      get () {
        return this.$store.getters['getSessionEnableTrainers']
      },
      set (value) {
        this.$store.dispatch('setSessionEnableTrainers', value)
      }
    },
    sessionCompletionType: {
      get () {
        return this.$store.getters['getSessionCompletionType']
      },
      set (value) {
        this.$store.dispatch('setSessionCompletionType', value)
      }
    },
    sessionCompletionEnableRegisters: {
      get () {
        return this.$store.getters['getSessionCompletionEnableRegisters']
      },
      set (value) {
        this.$store.dispatch('setSessionCompletionEnableRegisters', value)
      }
    },
    sessionCompletionRegistersEditByAnyCoordinator: {
      get () {
        return this.$store.getters['getSessionCompletionRegistersEditByAnyCoordinator']
      },
      set (value) {
        this.$store.dispatch('setSessionCompletionRegistersEditByAnyCoordinator', value)
      }
    },
    sessionCompletionRegistersEditByTrainers: {
      get () {
        return this.$store.getters['getSessionCompletionRegistersEditByTrainers']
      },
      set (value) {
        this.$store.dispatch('setSessionCompletionRegistersEditByTrainers', value)
      }
    },
    sessionCompletionFormRequired: {
      get () {
        return this.$store.getters['getSessionCompletionTrainerFeedbackRequired']
      },
      set (value) {
        this.$store.dispatch('setSessionCompletionFormRequired', value)
      }
    },
    sessionCompletionFormFields: {
      get () {
        return this.$store.getters['getSessionCompletionFormFields']
      }
    },
    sessionCompletionAbsenceReasonRequired: {
      get () {
        return this.$store.getters['getSessionCompletionAbsenceReasonRequired']
      },
      set (value) {
        this.$store.dispatch('setSessionCompletionAbsenceReasonRequired', value)
      }
    },
    sessionCompletionAbsenceReason: {
      get () {
        return this.$store.getters['getSessionCompletionAbsenceReason']
      },
      set (value) {
        this.$store.dispatch('setSessionCompletionAbsenceReason', value)
      }
    },
    watchContent () {
      return this.sessionEnableTrainers + this.sessionCompletionType + this.sessionCompletionEnableRegisters +
      this.sessionCompletionRegistersEditByAnyCoordinator + this.sessionCompletionRegistersEditByTrainers + this.sessionCompletionFormRequired +
      this.sessionCompletionFormFields + this.sessionCompletionAbsenceReasonRequired + this.sessionCompletionAbsenceReason
    },
    //
    sessionAllowEdit: function () {
      return this.$store.getters.getSessionAllowEdit
    }
  },
  methods: {
    sessionAddCompletionFormField: function (data) {
      this.$store.dispatch('setSessionCompletionFormField', data)
    },
    sessionEditCompletionFormField: function (data, index) {
      this.$store.dispatch('updateSessionCompletionFormField', { data: data, index: index })
    },
    sessionRemoveCompletionFormField: function (data) {
      this.$store.dispatch('removeSessionCompletionFormField', data)
    },
    sessionMoveCompletionFormField: function (data) {
      this.$store.dispatch('moveSessionCompletionFormField', data)
    },
    //
    setSessionCompletionType (value) {
      this.$store.dispatch('setSessionCompletionType', value)
    },
    sessionReset: function () {
      this.$store.dispatch('resetSession')
    },
    sessionSubmit: function () {
      this.$validator.validateAll().then((result) => {
        if (result) {
          if (this.$route.name.includes('ebs-session-add')) {
            this.$store.dispatch('createSession')
          } else if (this.$route.name.includes('ebs-session-edit')) {
            this.$store.dispatch('updateSession')
          }
          this.allowRedirectBeforeRouteLeave()
        } else {
          this.$refs.popnotifyvalidation.openNotify()
        }
      })
    }
  },
  inject: ['$validator']
}
</script>
